// Modal de firma digital Digilogix
import React, { useState, useEffect, useRef } from 'react';
import { firmaApi } from '../../services/api';

const ESTADO_LABEL = {
  1: { label: 'Pendiente',            color: 'warning', icon: 'hourglass-split'   },
  2: { label: 'Firmado parcialmente', color: 'info',    icon: 'pen'               },
  3: { label: 'Firmado',             color: 'success',  icon: 'check-circle-fill' },
};

const ESTADO_PERSONA = {
  1: 'No es su turno',
  2: 'Firma autorizada',
  3: 'Firmado',
  4: 'Debe autorizar',
  5: 'Rechazado',
  6: 'Error',
};

export default function FirmaDigitalModal({ arcId, arcNombre, onClose }) {
  const [step, setStep]       = useState('loading'); // loading | pending | done | error
  const [urlAuth, setUrlAuth] = useState(null);
  const [estado, setEstado]   = useState(null);
  const [estadoDetalle, setEstadoDetalle] = useState([]);
  const [error, setError]     = useState('');
  const pollRef               = useRef(null);

  useEffect(() => {
    iniciarFirma();
    return () => clearInterval(pollRef.current);
  }, [arcId]);

  useEffect(() => {
    clearInterval(pollRef.current);
    if (step === 'pending') {
      pollRef.current = setInterval(async () => {
        try {
          const r = await firmaApi.estado(arcId);
          setEstado(r.data.estado);
          setEstadoDetalle(r.data.estados || []);
          if (r.data.estado === 3) { clearInterval(pollRef.current); setStep('done'); }
        } catch {}
      }, 8000);
    }
  }, [step, arcId]);

  const iniciarFirma = async () => {
    setStep('loading'); setError('');
    try {
      // Ver si ya tiene proceso iniciado
      const info = await firmaApi.info(arcId);
      if (info.data?.Dig_EstadoFirma === 3) {
        setEstado(3); setStep('done'); return;
      }
      if (info.data?.Dig_URLAutorizacion) {
        setUrlAuth(info.data.Dig_URLAutorizacion);
        setEstado(info.data.Dig_EstadoFirma);
        setStep('pending'); return;
      }
      // Primera vez — iniciar
      const r = await firmaApi.iniciar(arcId);
      setUrlAuth(r.data.urlAutorizacion);
      setEstado(1);
      setStep('pending');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setStep('error');
    }
  };

  const handleRefresh = async () => {
    try {
      const r = await firmaApi.estado(arcId);
      setEstado(r.data.estado);
      setEstadoDetalle(r.data.estados || []);
      if (r.data.estado === 3) { clearInterval(pollRef.current); setStep('done'); }
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const estadoInfo = ESTADO_LABEL[estado];

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog" style={{ maxWidth: 520 }}>
        <div className="modal-content">

          <div className="modal-header py-2">
            <h6 className="modal-title mb-0">
              <i className="bi bi-pen me-2" />Firma Digital
            </h6>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <p className="text-muted mb-3" style={{ fontSize: '0.85rem' }}>
              <i className="bi bi-paperclip me-1" /><strong>{arcNombre}</strong>
            </p>

            {/* Loading */}
            {step === 'loading' && (
              <div className="text-center py-4">
                <span className="spinner-border spinner-border-sm me-2" />
                <span className="text-muted small">Iniciando proceso de firma...</span>
              </div>
            )}

            {/* Pendiente — mostrar link */}
            {step === 'pending' && (
              <div>
                {estadoInfo && (
                  <div className={`alert alert-${estadoInfo.color} d-flex align-items-center gap-2 py-2 mb-3`}>
                    <i className={`bi bi-${estadoInfo.icon}`} />
                    <strong>{estadoInfo.label}</strong>
                  </div>
                )}

                {urlAuth && (
                  <div className="mb-3">
                    <p className="mb-1 small text-muted">
                      Compartí este link con el firmante para que autorice la firma con su PIN y OTP:
                    </p>
                    <div className="d-flex gap-2 align-items-center">
                      <input
                        className="form-control form-control-sm font-monospace"
                        readOnly
                        value={urlAuth}
                        onClick={e => e.target.select()}
                        style={{ fontSize: '0.72rem' }}
                      />
                      <button className="btn btn-outline-secondary btn-sm"
                        title="Copiar"
                        onClick={() => navigator.clipboard.writeText(urlAuth)}>
                        <i className="bi bi-clipboard" />
                      </button>
                      <a href={urlAuth} target="_blank" rel="noreferrer"
                        className="btn btn-outline-primary btn-sm text-nowrap">
                        <i className="bi bi-box-arrow-up-right me-1" />Abrir
                      </a>
                    </div>
                  </div>
                )}

                {estadoDetalle.length > 0 && (
                  <table className="table table-sm mb-3" style={{ fontSize: '0.82rem' }}>
                    <thead><tr><th>Firmante</th><th>Estado</th></tr></thead>
                    <tbody>
                      {estadoDetalle.map((e, i) => (
                        <tr key={i}>
                          <td className="font-monospace">{e.CodigoUnicoIdentificacion}</td>
                          <td>{ESTADO_PERSONA[e.CodigoEstado] || e.DescripcionEstado}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={handleRefresh}>
                    <i className="bi bi-arrow-clockwise me-1" />Actualizar estado
                  </button>
                  <button className="btn btn-link btn-sm text-muted" onClick={iniciarFirma}>
                    Reiniciar
                  </button>
                </div>
              </div>
            )}

            {/* Firmado */}
            {step === 'done' && (
              <div className="text-center py-3">
                <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '3rem' }} />
                <p className="mt-2 mb-0">El documento fue firmado correctamente.</p>
              </div>
            )}

            {/* Error */}
            {step === 'error' && (
              <div>
                <div className="alert alert-danger">{error}</div>
                <button className="btn btn-outline-secondary btn-sm" onClick={iniciarFirma}>
                  <i className="bi bi-arrow-clockwise me-1" />Reintentar
                </button>
              </div>
            )}
          </div>

          <div className="modal-footer py-2">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
